/**
 * 提纯区：从任意粘贴的文案里把域名挑出来。
 *
 * 操作员拿到的通常是聊天记录、通知、表格导出混在一起的东西，里面夹着 IP、序号、
 * 日期、文件名。这里的规则是"宁可少挑，不要挑错"：
 *   - 只认最后一段是字母的（.cc / .top / .co.uk），纯数字结尾的一律不算 ——
 *     这样 IP（156.234.67.107）、版本号、日期都不会被当成域名。
 *   - 去掉列表序号前缀（"1.nioic.cc"）和 www.，只留注册域名本身。
 *   - 常见文件后缀直接跳过，避免把 logo.png 记成一个域名。
 * 分组顺序与组内顺序都保持原文出现的顺序，方便肉眼和原文对照。
 */

// 一眼就是文件/图片/代码的尾巴，不是待入库域名。
const FILE_SUFFIXES = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp', 'mp4', 'mp3', 'mov', 'avi', 'webm',
  'txt', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'zip', 'rar', '7z', 'tar',
  'gz', 'js', 'ts', 'tsx', 'jsx', 'css', 'html', 'htm', 'json', 'xml', 'yaml', 'yml', 'md', 'exe',
  'apk', 'iso', 'bin', 'log', 'sql', 'sh', 'bat', 'py', 'go', 'rb', 'php',
])

const CANDIDATE = /[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+/gi

export interface DomainGroup {
  suffix: string
  items: string[]
}

/** 从一段文本里挑出域名，按出现顺序去重。 */
export function extractDomains(text: string): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const m of text.matchAll(CANDIDATE)) {
    const d = normalizeCandidate(m[0])
    if (!d || seen.has(d)) continue
    seen.add(d)
    out.push(d)
  }
  return out
}

/** 把候选串规整成注册域名；不像域名就返回空串。 */
export function normalizeCandidate(raw: string): string {
  let labels = raw.toLowerCase().split('.').filter(Boolean)
  // 只在"剥掉后仍然像个域名"时才去掉列表序号前缀：1.nioic.cc → nioic.cc，
  // 而 8888.vip、003929.com 这种纯数字域名是真实存在的库存，不能顺手剥掉。
  while (labels.length > 2 && /^\d+$/.test(labels[0])) labels = labels.slice(1)
  if (labels.length && labels[0] === 'www') labels = labels.slice(1)
  if (labels.length < 2) return ''
  const tld = labels[labels.length - 1]
  // 顶级域必须是 2-24 个字母：数字结尾的是 IP/日期/版本号。
  if (!/^[a-z]{2,24}$/.test(tld)) return ''
  if (FILE_SUFFIXES.has(tld)) return ''
  if (labels.some((l) => l.length > 63)) return ''
  const domain = labels.join('.')
  return domain.length <= 253 ? domain : ''
}

// 二级后缀表：这些组合要当成一个整体后缀（.co.uk / .com.tw / .ne.jp …），
// 否则 shop.co.uk 会被归到 .uk 桶里，和真正的 .uk 域名混在一起。
const SECOND_LEVEL = new Set(['co', 'com', 'net', 'org', 'edu', 'gov', 'ac', 'ne', 'or', 'in'])

/** 取分组用的后缀：优先两段式公共后缀，否则最后一段。 */
export function suffixOf(domain: string): string {
  const labels = domain.split('.')
  const last = labels[labels.length - 1]
  if (last.length === 2 && labels.length >= 3 && SECOND_LEVEL.has(labels[labels.length - 2])) {
    return `.${labels.slice(-2).join('.')}`
  }
  return `.${last}`
}

/** 按后缀分组，组与组、组内都保持原文顺序。 */
export function groupBySuffix(domains: string[]): DomainGroup[] {
  const groups: DomainGroup[] = []
  const index = new Map<string, DomainGroup>()
  for (const d of domains) {
    const suffix = suffixOf(d)
    let g = index.get(suffix)
    if (!g) {
      g = { suffix, items: [] }
      index.set(suffix, g)
      groups.push(g)
    }
    g.items.push(d)
  }
  return groups
}

/** 复制用的排版：一组一竖列，组之间空一行。 */
export function formatGroups(groups: DomainGroup[]): string {
  return groups.map((g) => g.items.join('\n')).join('\n\n')
}
